import React from 'react';
import { Capacitor } from '@capacitor/core';
import { useSubscription } from '../../context/SubscriptionContext';
import { webPricesConfigured } from '../../lib/paidLaunch';
import { DataSyncPaywall } from './DataSyncPaywall';
import { SpendingToolsPaywall } from './SpendingToolsPaywall';

/**
 * Global paywall host. Renders the RC-Offerings-driven Spending Tools paywall
 * (card A1); the legacy $10 DataSyncPaywall remains only as the web degrade
 * when the Stripe price env vars are missing (it retires with the rest of the
 * freemium surfaces at G4).
 *
 * NATIVE: always the new paywall — RC Offerings drive pricing (card A1).
 *
 * WEB (card T2, landed): the new paywall ALSO renders on web, but only when
 * `webPricesConfigured()` — i.e. both
 * `VITE_STRIPE_SPENDING_MONTHLY_PRICE_ID` / `_ANNUAL_PRICE_ID` are set. There's
 * no RC Offering on web, so SpendingToolsPaywall builds its own package shape
 * from those env vars and checks out via Stripe Elements
 * (`createSubscription(..., { requestTrial })`) instead of an RC purchase.
 * Missing either env var is a deliberate degrade: stay on the legacy Stripe
 * paywall rather than risk shipping a broken checkout. (Trial-funnel
 * instrumentation for the new surface is card R1, not wired here yet.)
 */
export const PaywallModal: React.FC = () => {
  const { paywallState, hidePaywall, notifyPaywallPurchaseStarted } = useSubscription();
  const showNewPaywall = Capacitor.isNativePlatform() || webPricesConfigured();

  if (showNewPaywall) {
    return (
      <SpendingToolsPaywall
        isOpen={paywallState.isOpen}
        onClose={hidePaywall}
        onSuccess={paywallState.onPurchaseComplete}
        onPurchaseStart={notifyPaywallPurchaseStarted}
      />
    );
  }

  return (
    <DataSyncPaywall
      isOpen={paywallState.isOpen}
      onClose={hidePaywall}
      onSuccess={paywallState.onPurchaseComplete}
      source={paywallState.source}
      onPurchaseStart={notifyPaywallPurchaseStarted}
    />
  );
};
